import React, { useState, useContext, useMemo } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
    LoruleApplication,
    LoruleMenuConfig,
    LoruleComponentConfig,
    DropdownMenu,
} from '@galaxy/lorule-react-commons';
import { ToastContainer } from 'react-toastify';
import publicIp from 'public-ip';
import { Netmask } from 'netmask';
import { LoruleNavProps } from '@galaxy/lorule-react-commons/lib/js/components/LoruleNav';
import { Dropdown } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import ConfigContext from '../contexts/ConfigContext';
import AmRouter from './AmRouter';
import UserDelegate from './delegates/UserDelegate';
import { getEbsResponsibilities, getMyEmployeeId } from '../api/ebsUserApi';
import { getActionMemoCount } from '../api/actionMemoListApi';
import MyApprovalContext from '../contexts/MyApprovalContext';
import ActionMemoNavHeader, { ActionMemoMenuConfig } from './ActionMemoNavHeader';
import BudgetThreshold from './admin/budgetThreshold/BudgetThreshold';
import ContractApproversViewers from './admin/contractApproversViewers/ContractApproversViewers';
import ContractSigner from './admin/contractSigner/ContractSigner';
import Delegates from './admin/delegates/Delegates';
import TemplateType from './admin/templateType/TemplateType';
import TemplateMaintenance from './admin/templateMaintenance/TemplateMaintenance';
import OrganizationAuthorization from './admin/organizationAuthorization/OrganizationAuthorization';
import TemplateAuthorization from './admin/templateAuthorization/TemplateAuthorization';
import UserAuthorization from './admin/userAuthorization/UserAuthorization';
import EmployeeIdContext from '../contexts/EmployeeIdContext';
import { AM_LIST_PAGE_NAME, AM_LIST_PATH_NAME } from '../utils/Constants';

interface ActionMemoApplicationProps {
    msalInstance: PublicClientApplication;
}

const dropdownDividerComponent = (): LoruleComponentConfig => ({
    component: <Dropdown.Divider key={uuidv4()} />,
    name: '',
    path: '',
    roles: null,
});

const dropdownHeaderComponent = (name: string): LoruleComponentConfig => ({
    component: (
        <Dropdown.Header className="font-weight-bold" key={uuidv4()}>
            {name}
        </Dropdown.Header>
    ),
    name,
    path: '',
    roles: null,
});

const ActionMemoAppInternal = () => {
    const config = useContext(ConfigContext);
    const { nintendoIpSet, tableauDashboardUrl } = window;

    const [employeeId, setEmployeeId] = useState<number | null>(null);
    const [myApprovalCount, setMyApprovalCount] = useState<number | null>(null);

    const taskListTab: ActionMemoMenuConfig = {
        name: 'My Tasks',
        roles: ['ActionMemo.User', 'ActionMemo.Admin'],
        item: (
            <div>
                My Tasks
                {myApprovalCount && myApprovalCount > 0 ? (
                    <span className="ml-2 badge border border-white">{myApprovalCount}</span>
                ) : null}
            </div>
        ),
        components: [
            {
                name: 'My Tasks',
                path: '/tasks',
                exact: true,
                roles: null,
                component: <AmRouter />,
            },
        ],
    };

    const amListTab: ActionMemoMenuConfig = {
        name: AM_LIST_PAGE_NAME,
        roles: ['ActionMemo.User', 'ActionMemo.Admin'],
        components: [
            {
                name: AM_LIST_PAGE_NAME,
                path: AM_LIST_PATH_NAME,
                roles: null,
                component: <AmRouter />,
            },
        ],
    };

    const watchListTab: ActionMemoMenuConfig = {
        name: 'Favorites',
        roles: ['ActionMemo.User', 'ActionMemo.Admin'],
        components: [
            {
                name: 'Favorites',
                path: '/favorites',
                roles: null,
                component: <AmRouter />,
            },
        ],
    };

    const adminTab: ActionMemoMenuConfig = {
        name: 'Admin',
        roles: ['ActionMemo.Admin'],
        navCssClass: 'mobile-hide',
        components: [
            dropdownHeaderComponent('Budget'),
            {
                component: <BudgetThreshold />,
                name: 'Budget Threshold',
                path: '/admin/budgetThreshold',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Contracts'),
            {
                component: <ContractApproversViewers />,
                name: 'Contract Approver & Viewer',
                path: '/admin/contractApproversViewers',
                roles: null,
            },
            {
                component: <ContractSigner />,
                name: 'Contract Signer',
                path: '/admin/contractSigner',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Delegates'),
            {
                component: <Delegates />,
                name: 'Delegates',
                path: '/admin/delegate',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Templates'),
            {
                component: <TemplateType />,
                name: 'Template Type',
                path: '/admin/templateType',
                roles: null,
            },
            {
                component: <TemplateMaintenance />,
                name: 'Template',
                path: '/admin/templateMaintenance',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Authorizations'),
            {
                component: <OrganizationAuthorization />,
                name: 'Organization Authorization',
                path: '/admin/organizationAuthorization',
                roles: null,
            },
            {
                component: <TemplateAuthorization />,
                name: 'Template Authorization',
                path: '/admin/templateAuthorization',
                roles: null,
            },
            {
                component: <UserAuthorization />,
                name: 'View Authorization',
                path: '/admin/viewAuthorization',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Requests'),
            {
                component: <AmRouter />,
                name: 'Status Change Requests',
                path: '/admin/statusChangeRequest',
                roles: null,
            },
            dropdownDividerComponent(),
            dropdownHeaderComponent('Tools'),
            {
                component: <AmRouter />,
                name: 'Bulk User Management',
                path: '/admin/userManagement',
                roles: null,
            },
            {
                component: <AmRouter />,
                name: 'Project Errors',
                path: '/admin/projectErrorHandling',
                roles: null,
            },
        ],
    };

    const componentTabs: Array<LoruleMenuConfig> = [];
    componentTabs.push(taskListTab);
    componentTabs.push(amListTab);
    componentTabs.push(watchListTab);
    componentTabs.push(adminTab);

    const tabs = componentTabs.map((tab) => ({
        ...tab,
        components: tab.components.filter((component) => component.path),
    }));

    const hiddenRoutes: Array<LoruleComponentConfig> = [
        {
            name: 'Details',
            path: '/details',
            roles: null,
            component: <AmRouter />,
        },
        {
            name: 'Index',
            path: '/index',
            roles: null,
            component: <AmRouter />,
        },
    ];

    const userConfig: LoruleComponentConfig = {
        name: 'My Delegates',
        path: '/delegate',
        roles: null,
        component: <UserDelegate />,
    };

    const [dropDownMenus, setDropDownMenus] = useState<Array<DropdownMenu>>([]);

    const loadMyEmployeeId = async () => {
        setEmployeeId(await getMyEmployeeId(config));
    };

    const loadMyApproval = async () => {
        const count = await getActionMemoCount(config, {
            tasks: true,
        });

        setMyApprovalCount(count || 0);
    };

    const loadDropDownMenus = async () => {
        const clientIp = await publicIp.v4({
            fallbackUrls: ['https://ifconfig.co/ip'],
        });

        const nintendoIp = (nintendoIpSet as string)
            .split(',')
            .some((cidr) => new Netmask(cidr).contains(clientIp));
        if (nintendoIp) {
            const resps = await getEbsResponsibilities(config);
            setDropDownMenus([
                {
                    title: <>Galaxy Applications</>,
                    key: 'menu-dropdown',
                    items: [
                        {
                            item: (
                                <>
                                    Tableau Projects Dashboard{' '}
                                    <i className="fas fa-external-link-square-alt" />
                                </>
                            ),
                            testId: 'bi-dashboard-menu-dropdown',
                            onClick: () => {
                                window.open(
                                    `${tableauDashboardUrl}/views/ProjectOverviewSuperTables/ProjectsHomeLandingPage?:iid=1`,
                                    '_blank',
                                );
                            },
                            key: `bi-dashboard-menu-dropdown`,
                        },
                        ...(resps
                            ? resps.map((resp) => ({
                                  item: (
                                      <>
                                          Oracle EBS: {resp.responsibilityName}{' '}
                                          <i className="fas fa-external-link-square-alt" />
                                      </>
                                  ),
                                  testId: 'additional-menu-item-option',
                                  onClick: () => {
                                      window.open(resp.redirectUri, '_blank');
                                  },
                                  key: `${resp.responsibilityName}`,
                              }))
                            : []),
                    ],
                },
            ]);
        }
    };

    const myApprovalValue = useMemo(
        () => ({ approvalCount: myApprovalCount, refreshCount: loadMyApproval }),
        [],
    );

    const navHeaderCustom = (navProps: LoruleNavProps) => (
        <ActionMemoNavHeader {...navProps} componentTabs={componentTabs} />
    );

    return (
        <EmployeeIdContext.Provider value={employeeId}>
            <MyApprovalContext.Provider value={myApprovalValue}>
                <ToastContainer />
                <LoruleApplication
                    tabs={tabs}
                    appName="Action Memo"
                    authCallback={async () => {
                        await loadMyEmployeeId();
                        await loadDropDownMenus();
                        await loadMyApproval();
                    }}
                    defaultPath="/index"
                    routes={hiddenRoutes}
                    dropDownMenus={dropDownMenus}
                    userConfig={userConfig}
                    navHeaderCustom={navHeaderCustom}
                />
            </MyApprovalContext.Provider>
        </EmployeeIdContext.Provider>
    );
};

const ActionMemoApp = ({ msalInstance }: ActionMemoApplicationProps) => {
    const { actionMemoApiRequest, actionMemoApiUrl } = window;
    return (
        <MsalProvider instance={msalInstance}>
            <ConfigContext.Provider
                value={useMemo(
                    () => ({
                        msalInstance,
                        scopes: actionMemoApiRequest.scopes,
                        url: actionMemoApiUrl,
                    }),
                    [],
                )}
            >
                <ActionMemoAppInternal />
            </ConfigContext.Provider>
        </MsalProvider>
    );
};

export default ActionMemoApp;
